import { Injectable } from '@angular/core';
import {Apollo} from "apollo-angular";
import {Observable} from "rxjs";
import {SET_USER_SETTINGS} from "../mutations/SET_USER_SETTINGS";
import {TeacherSettings} from "../interfaces/forms/teacher-settings";
import {ADD_TEACHER_TO_CLASS} from "../mutations/ADD_TEACHER_TO_CLASS";
import {ADD_NEW_CLASS} from "../mutations/ADD_NEW_CLASS";
import {TeacherServerClassForm} from "../interfaces/teacher-server-class-form";
import {EDIT_CLASS} from "../mutations/EDIT_CLASS";
import {REMOVE_TEACHER_FROM_SCHOOL} from "../mutations/REMOVE_TEACHER_FROM_SCHOOL";
import {SEND_TEACHING_IDEA} from "../mutations/SEND_TEACHING_IDEA";
import {ADD_SCHOOL_TEACHER} from "../mutations/ADD_SCHOOL_TEACHER";
import {GET_SCHOOL_CLASSES_BY_USER} from "../queries/GET_SCHOOL_CLASSES_BY_USER";
import {DataService} from "./data.service";
import {DELETE_CLASS} from "../mutations/DELETE_CLASS";
import {WatchDataService} from "./watch-data.service";
import {INCREASE_JOB_MARKET_APPLICATION_COUNT} from "../mutations/INCREASE_JOB_MARKET_APPLICATION_COUNT";
import {REGISTER_ROOKIE_COMPANY} from "../mutations/REGISTER_ROOKIE_COMPANY";
import {ROOKIE_SCHOLAR_BOOKING} from "../mutations/ROOKIE_SCHOLAR_BOOKING";

@Injectable({
  providedIn: 'root'
})
export class MutationService {

  constructor(
    private apollo: Apollo,
    private watchDataService: WatchDataService
  ) { }


  setUserSettings(teacherSettings: TeacherSettings) {
    return new Observable<any>(observer => {
      this.apollo
        .use("internalClient")
        .mutate<any>({
          mutation: SET_USER_SETTINGS,
          fetchPolicy: "no-cache",
          variables: {
            formData: teacherSettings
          }
        })
        .subscribe(({data, loading}) => {
          if(data) {
            this.watchDataService.getUserInfoQuery?.refetch();
            observer.next(data.updateUser);
          } else {
            observer.next(undefined);
          }
          observer.complete();
        })
    })
  }

  addTeacherToClass(schoolClassId: number, teacherEmail: string) {
    return new Observable<any>(observer => {
      this.apollo
        .use("internalClient")
        .mutate<any>({
          mutation: ADD_TEACHER_TO_CLASS,
          fetchPolicy: "no-cache",
          variables: {
            schoolClassId,
            teacherEmail
          }
        })
        .subscribe(({data, loading}) => {
          if(data){
            // console.log("data", data);
            observer.next(data.addSchoolClassTeacher);
          } else {
            observer.next(undefined);
          }
          observer.complete();
        })
    })
  }

  addSchoolTeacher(teacherEmail: string) {
    return new Observable<any>(observer => {
      this.apollo
        .use("internalClient")
        .mutate<any>({
          mutation: ADD_SCHOOL_TEACHER,
          fetchPolicy: "no-cache",
          variables: {
            teacherEmail
          }
        })
        .subscribe(({data, loading}) => {
          if(data) {
            this.watchDataService.getSchoolTeachersQuery?.refetch();
            observer.next(data.addSchoolTeacher);
          } else {
            observer.next(undefined);
          }
          observer.complete();
        })
    })
  }

  addNewClass(data: Partial<TeacherServerClassForm>) {
    // console.log(data);
    return new Observable<any>(observer => {
      this.apollo
        .use("internalClient")
        .mutate<any>({
          mutation: ADD_NEW_CLASS,
          fetchPolicy: "no-cache",
          variables: {
            formData: data
          }
        })
        .subscribe(({data, loading}) => {
          if(data) {
            this.watchDataService.getSchoolClassesByUserQuery?.refetch();
            observer.next(data.addSchoolClass);
          } else {
            observer.next(undefined);
          }
          observer.complete();
        }, (error) => {
          console.error(error);
        })
    })
  }

  editClass(schoolClassId: number, data: Partial<TeacherServerClassForm>) {
    return new Observable<any>(observer => {
      this.apollo
        .use("internalClient")
        .mutate<any>({
          mutation: EDIT_CLASS,
          fetchPolicy: "no-cache",
          variables: {
            schoolClassID: schoolClassId,
            formData: data
          }
        })
        .subscribe(({data, loading}) => {
          if(data) {
            this.watchDataService.getSchoolClassesByUserQuery?.refetch();
            observer.next(data.updateSchoolClass);
          } else {
            observer.next(undefined);
          }
          observer.complete();
        }, (error) => {
          console.error(error);
        })
    })
  }

  removeTeacherFromSchool(teacherId: number) {
    return new Observable<any>(observer => {
      this.apollo
        .use("internalClient")
        .mutate<any>({
          mutation: REMOVE_TEACHER_FROM_SCHOOL,
          fetchPolicy: "no-cache",
          variables: {
            teacherId
          }
        })
        .subscribe(({data, loading}) => {
          if(data) {
            // console.log("data", data);
            this.watchDataService.getSchoolTeachersQuery?.refetch();
            observer.next(data.removeSchoolTeacher);
          } else {
            observer.next(undefined);
          }
          observer.complete();
        })
    })
  }

  sendTeachingIdea(teachingIdeaFeedback: any, classId: number) {
    return new Observable<any>(observer => {
      this.apollo
        .use("internalClient")
        .mutate<any>({
          mutation: SEND_TEACHING_IDEA,
          fetchPolicy: "no-cache",
          variables: {
            classId,
            formData: teachingIdeaFeedback
          }
        })
        .subscribe(({data, loading}) => {
          if(data){
            // console.log("data", data);
            observer.next(data.sendTeachingIdea);
          } else {
            observer.next(undefined);
          }
          observer.complete();
        })
    })
  }

  deleteClass(schoolClassId: number) {
    return new Observable<any>(observer => {
      this.apollo
        .use("internalClient")
        .mutate<any>({
          mutation: DELETE_CLASS,
          fetchPolicy: "no-cache",
          variables: {
            schoolClassID: schoolClassId
          }
        })
        .subscribe(({data, loading}) => {
          if(data){
            // console.log("data", data);
            this.watchDataService.getSchoolTeachersQuery?.refetch();
            this.watchDataService.getSchoolClassesByUserQuery?.refetch();
            observer.next(data.removeSchoolClass);
          } else {
            observer.next(undefined);
          }
          observer.complete();
        })
    })
  }

  increaseJobMarketApplicationCount(jobMarketId: number) {
    return new Observable<any>(observer => {
      this.apollo
        .mutate<any>({
          mutation: INCREASE_JOB_MARKET_APPLICATION_COUNT,
          fetchPolicy: "no-cache",
          variables: {
            jobMarketId
          }
        })
        .subscribe(({data, loading}) => {
          if(data){
            observer.next(data.increaseJobMarketApplicationCount);
          } else {
            observer.next(undefined);
          }
          observer.complete();
        })
    })
  }

  registerRookieCompany(
    districtId: number,
    companyId: number,
    companyName: string,
    companyStreet: string,
    companyZip: string,
    companyCity: string,
    contactFirstname: string,
    contactLastname: string,
    contactPhone: string,
    contactEmail: string,
    jobEntries: any[]
  ) {
    return new Observable<any>(observer => {
      this.apollo
        .mutate<any>({
          mutation: REGISTER_ROOKIE_COMPANY,
          fetchPolicy: "no-cache",
          variables: {
            districtId,
            companyId,
            companyName,
            companyStreet,
            companyZip,
            companyCity,
            contactFirstname,
            contactLastname,
            contactPhone,
            contactEmail,
            jobEntries
          }
        })
        .subscribe(({data, loading}) => {
          if(data){
            observer.next(data.rookieRegisterCompany);
          } else {
            observer.next(undefined);
          }
          observer.complete();
        })
    })
  }

  rookieScholarBooking(
    districtId: number,
    positionId: number,
    schoolId: number,
    classId: number,
    selectedTimeSlots: number[],
    firstName: string,
    lastName: string,
    email: string,
    phoneNumber: string,
    address: string,
    zip: string,
    location: string,
    privacyBoxSelected: boolean
  ) {
    return new Observable<any>(observer => {
      this.apollo
        .mutate<any>({
          mutation: ROOKIE_SCHOLAR_BOOKING,
          fetchPolicy: "no-cache",
          variables: {
            districtId,
            positionId,
            schoolId,
            classId,
            selectedTimeSlots,
            firstName,
            lastName,
            email,
            phoneNumber,
            address,
            zip,
            location,
            privacyBoxSelected
          }
        })
        .subscribe(({data, loading}) => {
          if(data){
            observer.next(data.rookieScholarBooking);
          } else {
            observer.next(undefined);
          }
          observer.complete();
        })
    })
  }

}
